import { useQuery } from '@tanstack/react-query';
import { partition } from 'lodash';
import { request } from 'javascripts/services/v3ApiClient';
import useNewProjectTask from 'components/projects/projectTasks/hooks/useNewProjectTask';
import { combineExistingTasksWithNewTask } from 'components/projects/projectTasks/hooks/helpers';
import type {
  TaskObject,
  TaskImageObject,
  TaskVideoObject,
} from 'data/v3/data-contracts';
import type {
  V3ApiSuccessData,
  V3ApiError,
} from 'javascripts/services/v3ApiClient';
import { ASSET_TYPE } from '../constants';
import { ProjectDatabaseId } from 'data/v1';

export const ASSET_PROJECT_TASKS_QUERY_KEY = 'AssetProjectTasks';
export const PROJECT_TASKS_COUNT_QUERY_KEY = 'ProjectTasksCount';

type ProjectTasksCount = {
  tasksCount: number;
  tasksCompletedCount: number;
};

export type GetTasksCountResponse = V3ApiSuccessData<ProjectTasksCount>;
export type GetAssetProjectTasksResponse = V3ApiSuccessData<TaskObject[]>;

type UseGetAssetProjectTasksProps = {
  projectDbId: ProjectDatabaseId;
  assetDbId: TaskImageObject['id'] | TaskVideoObject['id'];
  assetType: string;
};

export const useGetAssetProjectTasks = ({
  projectDbId,
  assetDbId,
  assetType,
}: UseGetAssetProjectTasksProps) => {
  const VIDEO_PROJECT_TASKS_URL = `projects/${projectDbId}/videos/${assetDbId}/tasks`;
  const PHOTO_PROJECT_TASKS_URL = `projects/${projectDbId}/photos/${assetDbId}/tasks`;

  const { data, isLoading, error } = useQuery<
    GetAssetProjectTasksResponse,
    V3ApiError
  >({
    queryFn: async () => {
      const response = await request({
        url:
          assetType === ASSET_TYPE.PHOTO
            ? PHOTO_PROJECT_TASKS_URL
            : VIDEO_PROJECT_TASKS_URL,
      });
      return response.data;
    },
    queryKey: [ASSET_PROJECT_TASKS_QUERY_KEY, assetDbId],
  });

  const { data: tasksCountData } = useQuery<GetTasksCountResponse, V3ApiError>({
    queryFn: async () => {
      const response = await request({
        url: `projects/${projectDbId}/tasks/counts`,
      });
      return response.data;
    },
    queryKey: [PROJECT_TASKS_COUNT_QUERY_KEY, projectDbId],
  });

  const totalProjectTasksCount = tasksCountData?.data.tasksCount;

  const projectTasks = data?.data || [];

  const [completedTasks, incompleteTasks] = partition(
    projectTasks,
    'completed',
  );

  const sortedCompletedTasks = [...completedTasks].sort(
    (a, b) => Number(new Date(b.completedAt)) - Number(new Date(a.completedAt)),
  );

  const { newTask, addNewTask, addNewTaskAtEnd, removeNewTask } =
    useNewProjectTask({
      lastPosition: totalProjectTasksCount,
      lastIndex: incompleteTasks.length,
    });

  return {
    incompleteTasks: combineExistingTasksWithNewTask({
      newTask,
      existingTasks: incompleteTasks,
    }),
    completedTasks: sortedCompletedTasks,
    addNewTask,
    addNewTaskAtEnd,
    removeNewTask,
    loading: isLoading,
    error,
  };
};

export default useGetAssetProjectTasks;
